import { builder, Builder } from "@builder.io/sdk";
import { componentConfigs } from "@/components/builder";

// General components
import CustomJsonLd from "@/components/custom-json-ld";
import Awards from "@/components/sections/awards";
import TestimonialsProfessionals from "@/components/sections/testimonials-professionals";

// Home page components
import Hero from "@/components/sections/hero";
import Testimonials from "@/components/sections/testimonials";
import ContactHome from "@/components/sections/contact-home";
import BuildRewardingCareer from "@/components/sections/build-rewarding-career";
import ServicesWeOffer from "@/components/sections/services-we-offer";

// Services page components
import Header from "@/components/sections/header";
import ProductDescription from "@/components/sections/product-description";
import TextCardGroup from "@/components/sections/text-card-group";
import Benefits from "@/components/sections/benefits";
import CTAServices from "@/components/sections/cta-services";
import FAQs from "@/components/sections/faqs";
import TwoColumnText from "@/components/sections/two-column-text";
import TypesOfMedicalStaffingSolutions from "@/components/sections/types-of-medical-staffing-solutions";

// Locations page components
import HeaderLocations from "@/components/sections/header-locations";
import AwardsTestimonials from "@/components/sections/awards-testimonials";
import OtherLocations from "@/components/sections/other-locations";
import FindALocation from "@/components/locations/find-a-location";
import OfficeLocations from "@/components/sections/office-locations";
import LocationsServices from "@/components/sections/locations-services";
import LocationCTA from "@/components/locations/cta";

// Careers page components
import HeaderCareers from "@/components/sections/header-careers";
import CareersCTA from "@/components/careers/cta";
import CareersApplicationForm from "@/components/forms/careers-application-form";
import CareersFAQs from "@/components/careers/faqs";
import CareersData from "@/components/careers/careers-data";
import CareersCIEnvironment from "@/components/careers/careers-ci-environment";
import CareersAboutUs from "@/components/careers/careers-about-us";

// Forms components
import FormInquiryPromotion from "@/components/sections/form-inquiry-promotion";
import NewsletterFormLarge from "@/components/forms/newsletter-form-large";
// CI App components
import CIAppDemoRequest from "@/components/forms/ci-app-demo-request-form";
import CIAppDemoForm from "@/components/forms/ci-app-demo-form";
import KeyFeatures from "@/components/ci-app/key-features";
import HeroCiApp from "@/components/sections/hero-ci-app";
import FAQsCIApp from "@/components/sections/faqs-ci-app";
import FooterCiApp from "@/components/ci-app/footer-ci-app";

// CI Community components
import FeaturesCIVR from "@/components/sections/features-ci-vr";
import BenefitsCIVR from "@/components/sections/benefits-ci-vr";
import InquiryCIVR from "@/components/sections/inquiry-ci-vr";
import HeaderBlogsEventsClasses from "@/components/sections/header-blogs-events-classes";
import BenefitsCIION from "@/components/sections/benefits-ci-ion";
import MissionVisionPurposeCIION from "@/components/sections/mission-vision-purpose-ci-ion";

// Calendly components
import CalendlyWidget from "@/components/sections/calendly-widget";
import CalendlyThankYouGeneric from "@/components/sections/calendly-thank-you-generic";

// Initialize Builder with API key
builder.init(process.env.NEXT_PUBLIC_BUILDER_API_KEY!);

// Register editor settings
Builder.register("editor.settings", {
  styleStrictMode: false,
  hideHeatmap: true,
  designTokens: {
    backgroundColor: [
      { name: "White", value: "#FFFFFF" },
      { name: "Orange-Light", value: "#FFFAF7" },
      { name: "Teal-Light", value: "#F7FEFF" },
      { name: "Teal-Dark", value: "#00797D" },
      { name: "Gray", value: "#FAF8F8" },
    ],
    color: [
      { name: "White", value: "#FFFFFF" },
      { name: "Primary-100", value: "#FFFAF7" },
      { name: "Primary-200", value: "#FFEEE5" },
      { name: "Primary-300", value: "#FFD5BF" },
      { name: "Primary-400", value: "#E56E2E" },
      { name: "Primary-500", value: "#C74601" },
      { name: "Primary-600", value: "#421700" },
      { name: "Secondary-100", value: "#F7FEFF" },
      { name: "Secondary-200", value: "#E5FEFF" },
      { name: "Secondary-300", value: "#C4F4F5" },
      { name: "Secondary-400", value: "#06A6AB" },
      { name: "Secondary-500", value: "#00797D" },
      { name: "Secondary-600", value: "#004142" },
      { name: "Neutral-100", value: "#FAF8F8" },
      { name: "Neutral-200", value: "#E5E4E3" },
      { name: "Neutral-300", value: "#D9D6D5" },
      { name: "Neutral-400", value: "#7A7470" },
      { name: "Neutral-500", value: "#524D4B" },
      { name: "Neutral-600", value: "#1F1C1B" },
      { name: "Sentiment-positive-100", value: "#E5F4EE" },
      { name: "Sentiment-positive-200", value: "#73C5A3" },
      { name: "Sentiment-positive-300", value: "#00854D" },
      { name: "Sentiment-warning-100", value: "#FFF9E5" },
      { name: "Sentiment-warning-200", value: "#FFE073" },
      { name: "Sentiment-warning-300", value: "#FFC700" },
      { name: "Sentiment-negative-100", value: "#FBE6E6" },
      { name: "Sentiment-negative-200", value: "#E97474" },
      { name: "Sentiment-negative-300", value: "#D70101" },
    ],
    fontFamily: [
      { name: "Montserrat", value: "var(--font-montserrat)" },
      { name: "Roboto", value: "var(--font-roboto)" },
    ],
    fontSize: [
      { name: "Heading-1", value: "64px" },
      { name: "Heading-2", value: "40px" },
      { name: "Heading-3", value: "32px" },
      { name: "Heading-4", value: "24px" },
      { name: "SubHeading-1", value: "32px" },
      { name: "SubHeading-2", value: "24px" },
      { name: "SubHeading-3", value: "16px" },
      { name: "Body-1", value: "16px" },
      { name: "Body-2", value: "12px" },
      { name: "Body-3", value: "8px" },
    ],
    fontWeight: [
      { name: "Medium (500)", value: "500" },
      { name: "Regular (400)", value: "400" },
      { name: "Light (300)", value: "300" },
    ],
    lineHeight: [
      { name: "Heading-1", value: "72px" },
      { name: "Heading-2", value: "48px" },
      { name: "Heading-3", value: "40px" },
      { name: "Heading-4", value: "32px" },
      { name: "SubHeading-1", value: "40px" },
      { name: "SubHeading-2", value: "32px" },
      { name: "SubHeading-3", value: "24px" },
      { name: "Body-1", value: "24px" },
      { name: "Body-2", value: "16px" },
      { name: "Body-3", value: "12px" },
    ],
  },
});

// Register insert menus
Builder.register("insertMenu", {
  name: "General",
  items: [
    { name: "CustomJsonLd" },
    { name: "Awards" },
    { name: "Testimonials Professionals" },
  ],
});

Builder.register("insertMenu", {
  name: "Home",
  items: [
    { name: "Hero (Home)" },
    { name: "Testimonials (Home)" },
    { name: "Services We Offer (Home)" },
    { name: "Contact Home (Home)" },
    { name: "Build Rewarding Career (Home)" },
  ],
});

Builder.register("insertMenu", {
  name: "Services",
  items: [
    { name: "Header" },
    { name: "Product Description" },
    { name: "Text Card Group" },
    { name: "Benefits" },
    { name: "CTA Services" },
    { name: "FAQs" },
    { name: "Two Column Text" },
    { name: "Types Of Medical Staffing Solutions" },
  ],
});

Builder.register("insertMenu", {
  name: "Locations",
  items: [
    { name: "Header Locations" },
    { name: "Awards Testimonials" },
    { name: "Other Locations" },
    { name: "Find a Location" },
    { name: "Office Locations" },
    { name: "Location Services" },
    { name: "Location CTA" },
  ],
});

Builder.register("insertMenu", {
  name: "Careers",
  items: [
    { name: "Careers Headers" },
    { name: "Careers About Us" },
    { name: "Careers CTA" },
    { name: "Careers Application Form" },
    { name: "Careers FAQs" },
    { name: "Careers Data" },
    { name: "Careers CI Environment" },
  ],
});

Builder.register("insertMenu", {
  name: "Custom Forms",
  items: [
    { name: "Form Inquiry Promotion" },
    { name: "CI App Demo Form" },
    { name: "Newsletter Form Large" },
  ],
});

Builder.register("insertMenu", {
  name: "CI-App",
  items: [
    { name: "Hero CI App" },
    { name: "CI App Demo Request" },
    { name: "Key Features" },
    { name: "FAQs CI App" },
    { name: "Footer CI App" },
  ],
});

Builder.register("insertMenu", {
  name: "CI Community",
  items: [
    { name: "Header Blogs Events Classes" },
    { name: "Features CI-VR" },
    { name: "Benefits CI-VR" },
    { name: "Inquiry CI-VR" },
    { name: "Benefits CI-ION" },
    { name: "Mission Vision Purpose CI-ION" },
  ],
});

Builder.register("insertMenu", {
  name: "Calendly",
  items: [
    { name: "Calendly Widget" },
    { name: "Calendly Thank You" },
  ],
});

// Register components by category
// General components
Builder.registerComponent(CustomJsonLd, componentConfigs.customJsonLd);
Builder.registerComponent(Awards, componentConfigs.awards);
Builder.registerComponent(
  TestimonialsProfessionals,
  componentConfigs.testimonialsProfessionals
);

// Home page components
Builder.registerComponent(Hero, componentConfigs.hero);
Builder.registerComponent(Testimonials, componentConfigs.testimonials);
Builder.registerComponent(ContactHome, componentConfigs.contactHome);
Builder.registerComponent(ServicesWeOffer, componentConfigs.servicesWeOffer);
Builder.registerComponent(
  BuildRewardingCareer,
  componentConfigs.buildRewardingCareer
);

// Services page components
Builder.registerComponent(Header, componentConfigs.header);
Builder.registerComponent(
  ProductDescription,
  componentConfigs.productDescription
);
Builder.registerComponent(TextCardGroup, componentConfigs.textCardGroup);
Builder.registerComponent(Benefits, componentConfigs.benefits);
Builder.registerComponent(CTAServices, componentConfigs.ctaServices);
Builder.registerComponent(FAQs, componentConfigs.faqs);
Builder.registerComponent(TwoColumnText, componentConfigs.twoColumnText);
Builder.registerComponent(
  TypesOfMedicalStaffingSolutions,
  componentConfigs.typesOfMedicalStaffingSolutions
);

// Locations page components
Builder.registerComponent(HeaderLocations, componentConfigs.headerLocations);
Builder.registerComponent(
  AwardsTestimonials,
  componentConfigs.awardsTestimonials
);
Builder.registerComponent(OtherLocations, componentConfigs.otherLocations);
Builder.registerComponent(FindALocation, componentConfigs.findALocation);
Builder.registerComponent(OfficeLocations, componentConfigs.officeLocations);
Builder.registerComponent(
  LocationsServices,
  componentConfigs.locationsServices
);
Builder.registerComponent(LocationCTA, componentConfigs.locationCTA);

// Careers page components
Builder.registerComponent(HeaderCareers, componentConfigs.headerCareers);
Builder.registerComponent(CareersCTA, componentConfigs.careersCTA);
Builder.registerComponent(
  CareersApplicationForm,
  componentConfigs.careersApplicationForm
);
Builder.registerComponent(CareersFAQs, componentConfigs.careersFAQs);
Builder.registerComponent(CareersData, componentConfigs.careersData);
Builder.registerComponent(
  CareersCIEnvironment,
  componentConfigs.careersCIEnvironment
);
Builder.registerComponent(CareersAboutUs, componentConfigs.careersAboutUs);

// Forms components
Builder.registerComponent(
  FormInquiryPromotion,
  componentConfigs.formInquiryPromotion
);
Builder.registerComponent(
  NewsletterFormLarge,
  componentConfigs.newsletterFormLarge
);

// CI App components
Builder.registerComponent(CIAppDemoRequest, componentConfigs.ciAppDemoRequest);
Builder.registerComponent(CIAppDemoForm, componentConfigs.ciAppDemoForm);
Builder.registerComponent(KeyFeatures, componentConfigs.keyFeatures);
Builder.registerComponent(HeroCiApp, componentConfigs.heroCiApp);
Builder.registerComponent(FAQsCIApp, componentConfigs.faqsCiApp);
Builder.registerComponent(FooterCiApp, componentConfigs.footerCiApp);

// CI Community components
Builder.registerComponent(
  HeaderBlogsEventsClasses,
  componentConfigs.headerBlogsEventsClasses
);
Builder.registerComponent(FeaturesCIVR, componentConfigs.featuresCiVr);
Builder.registerComponent(BenefitsCIVR, componentConfigs.benefitsCiVr);
Builder.registerComponent(InquiryCIVR, componentConfigs.inquiryCiVr);
Builder.registerComponent(BenefitsCIION, componentConfigs.benefitsCiIon);
Builder.registerComponent(
  MissionVisionPurposeCIION,
  componentConfigs.missionVisionPurposeCiIon
);

// Calendly components
Builder.registerComponent(CalendlyWidget, componentConfigs.calendlyWidget);
Builder.registerComponent(CalendlyThankYouGeneric, componentConfigs.calendlyThankYouGeneric);
